import type { Relation } from '../model';
import type { LabelText } from './label-placement-helper';
import {
  LayoutGeometryHelper as G,
  type NodeFrame,
} from './layout-geometry-helper';
import type {
  DraftLine,
  PlacedNode,
  Point,
  Rect,
  ShareLabel,
} from './structure-layout.types';

export interface LoopObstacles {
  nodes: ReadonlyArray<PlacedNode>;
  lines: ReadonlyArray<DraftLine>;
  annotations: ReadonlyArray<Rect>;
}

export interface LoopRouting {
  lines: DraftLine[];
  labels: ShareLabel[];
  blocked: Relation[];
}

class LoopChannelHelper {
  static readonly CHANNEL_GAP = 48;

  // A loop whose side lines would cross something comes back as blocked, to be shown as chips.
  static route(
    loops: ReadonlyArray<Relation>,
    obstacles: Readonly<LoopObstacles>,
    labelOf: (relation: Readonly<Relation>) => LabelText,
    frame: Readonly<NodeFrame> = G.CARD_FRAME
  ): LoopRouting {
    const nodeById = new Map(
      obstacles.nodes.map((node) => [node.entityId, node])
    );
    const segments = obstacles.lines.flatMap((line) =>
      LoopChannelHelper.toSegments(line.points)
    );
    const annotations = [...obstacles.annotations];
    const routing: LoopRouting = { lines: [], labels: [], blocked: [] };
    loops.forEach((relation) => {
      const owner = nodeById.get(relation.ownerId);
      const owned = nodeById.get(relation.ownedId);
      const route =
        owner && owned
          ? LoopChannelHelper.tryRoute(
              relation,
              owner,
              owned,
              labelOf(relation),
              obstacles.nodes,
              segments,
              annotations,
              frame
            )
          : null;
      if (!route) {
        routing.blocked.push(relation);
        return;
      }
      routing.lines.push(route.line);
      routing.labels.push(route.label);
      segments.push(...LoopChannelHelper.toSegments(route.line.points));
      annotations.push(route.label);
    });
    return routing;
  }

  private static tryRoute(
    relation: Readonly<Relation>,
    owner: Readonly<PlacedNode>,
    owned: Readonly<PlacedNode>,
    label: Readonly<LabelText>,
    nodes: ReadonlyArray<PlacedNode>,
    segments: ReadonlyArray<Rect>,
    annotations: ReadonlyArray<Rect>,
    frame: Readonly<NodeFrame>
  ): { line: DraftLine; label: ShareLabel } | null {
    const startY = owner.y + owner.height / 2;
    const endY = owned.y + owned.height / 2;
    const labelY = Math.round((startY + endY - G.LABEL_HEIGHT) / 2);
    const top = Math.min(startY, endY, labelY) - G.LABEL_CLEARANCE;
    const bottom =
      Math.max(startY, endY, labelY + G.LABEL_HEIGHT) + G.LABEL_CLEARANCE;
    const inBand = (rect: Readonly<Rect>) =>
      rect.y <= bottom && rect.y + rect.height >= top;
    const contentRight = Math.max(
      ...[...nodes, ...segments, ...annotations]
        .filter(inBand)
        .map((rect) => rect.x + rect.width)
    );
    const labelX = contentRight + LoopChannelHelper.CHANNEL_GAP;
    const channelX = Math.round(labelX + label.width / 2);
    const points = G.simplify([
      {
        x: owner.x + owner.width - frame.sideInsetOf(owner.entityId),
        y: startY,
      },
      { x: channelX, y: startY },
      { x: channelX, y: endY },
      {
        x: owned.x + owned.width - frame.sideInsetOf(owned.entityId),
        y: endY,
      },
    ]);
    const clearance = annotations.map((rect) =>
      LoopChannelHelper.inflate(rect, G.LABEL_CLEARANCE)
    );
    const blocked = points
      .slice(1)
      .some((point, i) =>
        [...nodes, ...clearance].some((rect) =>
          G.segmentCrossesRect(points[i], point, rect)
        )
      );
    if (blocked) return null;
    return {
      line: { relationId: relation.id, kind: 'loop', points },
      label: {
        id: `label:${relation.id}`,
        relationId: relation.id,
        text: label.text,
        x: labelX,
        y: labelY,
        width: label.width,
        height: G.LABEL_HEIGHT,
      },
    };
  }

  private static toSegments(points: ReadonlyArray<Point>): Rect[] {
    return points
      .slice(1)
      .map((point, i) => G.pointsToRect([points[i], point]));
  }

  private static inflate(rect: Readonly<Rect>, by: number): Rect {
    return {
      x: rect.x - by,
      y: rect.y - by,
      width: rect.width + 2 * by,
      height: rect.height + 2 * by,
    };
  }
}

export { LoopChannelHelper };
